/**
 * Self-contained helpers for the admin auction-schedule editor — ports of the
 * web `@shared/utils` cn, the CHF currency formatter, the `@shared/utils/timezone`
 * Ukraine helpers and `@shared/utils/dateOffset` getEffectiveEndDate. date-fns /
 * MUI are intentionally NOT pulled in; formatting uses the native Intl APIs.
 *
 * The admin editor always renders the ADMIN view (real end times, no user-facing
 * role offset), so getEffectiveEndDate here applies no offset.
 */
import { ApiError } from '../../lib/api';
import type { AuctionScheduleItem, TimelineGroup } from './types';

export const UKRAINE_TIMEZONE = 'Europe/Kiev';

/**
 * True when `error` is an `ApiError` carrying a 401/403 status — i.e. the
 * operator is unauthenticated or lacks the role for this feature's endpoints
 * (`GET /admin/auction-schedule`, `GET /auction-sessions`, both admin-only).
 * Shared so the legacy board and the session-schedule tabs render the same
 * `<AccessDenied />` instead of the generic error panel, whose "Оновити"
 * button can never succeed against a 403.
 */
export function isAccessDeniedError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/** Join truthy class names (stand-in for the web `cn`). */
export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

/** CHF, no fraction digits — matches the web components' formatCurrency. */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Current time expressed in the Ukraine timezone (web `nowInUkraine`). */
export function nowInUkraine(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: UKRAINE_TIMEZONE }));
}

/**
 * Effective auction end date for the ADMIN view: parse the stored UTC value and
 * convert to Ukraine time with NO role offset (web `getEffectiveEndDate(date,
 * true)`).
 */
export function getEffectiveEndDate(date: string | Date): Date {
  const utcDate =
    typeof date === 'string' ? new Date(date.endsWith('Z') ? date : `${date}Z`) : new Date(date);
  return new Date(utcDate.toLocaleString('en-US', { timeZone: UKRAINE_TIMEZONE }));
}

/** `yyyy-MM-dd` key from a Date's local parts (web used date-fns `format`). */
export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Relative "in 3 days" / "5 hours ago" (stand-in for date-fns formatDistanceToNow). */
export function formatTimeRemaining(endDate: string, endedLabel: string): string {
  if (!endDate) return 'Invalid Date';
  const end = new Date(endDate);
  if (isNaN(end.getTime())) return 'Invalid Date';

  const now = nowInUkraine();
  const endInUkraine = new Date(end.toLocaleString('en-US', { timeZone: UKRAINE_TIMEZONE }));
  if (endInUkraine <= now) return endedLabel;

  const diffMs = endInUkraine.getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  const minutes = Math.round(diffMs / 60000);
  const hours = Math.round(diffMs / 3_600_000);
  const days = Math.round(diffMs / 86_400_000);
  if (Math.abs(days) >= 1) return rtf.format(days, 'day');
  if (Math.abs(hours) >= 1) return rtf.format(hours, 'hour');
  return rtf.format(minutes, 'minute');
}

/** Bid-management target: ended auctions link to the archive, else the bid page. */
export function getBidManagementUrl(auction: AuctionScheduleItem): string {
  return auction.status === 'ended'
    ? `/archive?car=${auction.listing_id}`
    : `/bids/${auction.listing_id}`;
}

/** Group auctions into Today / This Week / Later buckets (web timeline logic). */
export function groupAuctionsByTimeframe(
  auctions: AuctionScheduleItem[],
  titles: { today: string; thisWeek: string; later: string },
): TimelineGroup[] {
  const now = nowInUkraine();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

  const todayGroup: TimelineGroup = { title: titles.today, items: [], timeframe: 'today' };
  const weekGroup: TimelineGroup = { title: titles.thisWeek, items: [], timeframe: 'thisWeek' };
  const laterGroup: TimelineGroup = { title: titles.later, items: [], timeframe: 'later' };

  auctions.forEach((auction) => {
    if (!auction.end_date) return;
    const endDate = new Date(auction.end_date);
    if (isNaN(endDate.getTime())) return;
    const endInUkraine = new Date(endDate.toLocaleString('en-US', { timeZone: UKRAINE_TIMEZONE }));
    if (endInUkraine >= today && endInUkraine < tomorrow) todayGroup.items.push(auction);
    else if (endInUkraine >= tomorrow && endInUkraine < nextWeek) weekGroup.items.push(auction);
    else if (endDate >= nextWeek) laterGroup.items.push(auction);
  });

  return [todayGroup, weekGroup, laterGroup].filter((group) => group.items.length > 0);
}
